import { useState, useMemo } from "react";
import { JSONTree } from "react-json-tree";
import type { SourceSnapshot, Item } from "@tuit/shared-types";
import styles from "../../styles/components/AdminPage.module.css";
import { relativeTime } from "./utils";

type ItemSortCol = "title" | "domain" | "score" | "publishedAt";
type ItemSortDir = "asc" | "desc";

interface Props {
  snapshot: SourceSnapshot | null;
  sourceId: string;
  loading?: boolean;
}

function SortTh({
  col,
  sort,
  onSort,
  children,
}: {
  col: ItemSortCol;
  sort: { col: ItemSortCol; dir: ItemSortDir };
  onSort: (col: ItemSortCol) => void;
  children: React.ReactNode;
}) {
  const active = sort.col === col;
  return (
    <th
      className={`${styles.thSortable} ${active ? styles.thActive : ""}`}
      onClick={() => onSort(col)}
    >
      {children}
      <span className={styles.sortArrow}>
        {active ? (sort.dir === "asc" ? " ↑" : " ↓") : " ↕"}
      </span>
    </th>
  );
}

function SourceTableContent({ snapshot }: { snapshot: SourceSnapshot }) {
  const [sort, setSort] = useState<{ col: ItemSortCol; dir: ItemSortDir }>({
    col: "publishedAt",
    dir: "desc",
  });

  function handleSort(col: ItemSortCol) {
    setSort((prev) =>
      prev.col === col ? { col, dir: prev.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }
    );
  }

  const displayed = useMemo(() => {
    return [...snapshot.items].sort((a: Item, b: Item) => {
      let cmp = 0;
      switch (sort.col) {
        case "title":       cmp = a.title.localeCompare(b.title); break;
        case "domain":      cmp = a.domain.localeCompare(b.domain); break;
        case "score":       cmp = (a.score ?? 0) - (b.score ?? 0); break;
        case "publishedAt": cmp = new Date(a.publishedAt).getTime() - new Date(b.publishedAt).getTime(); break;
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [snapshot, sort]);

  return (
    <div>
      <div className={styles.metaBar}>
        <div className={styles.metaEntry}>
          <span className={`${styles.pip} ${styles[`pip_${snapshot.fetchStatus}`]}`} />
          <span>{snapshot.fetchStatus}</span>
        </div>
        <div className={styles.metaEntry}>
          <span className={styles.metaKey}>Fetched</span>
          <span>{relativeTime(snapshot.fetchedAt)}</span>
        </div>
        <div className={styles.metaEntry}>
          <span className={styles.metaKey}>Items</span>
          <span>{snapshot.items.length}</span>
        </div>
        {snapshot.errorMessage && (
          <div className={styles.metaEntry}>
            <span className={styles.opsResultError}>{snapshot.errorMessage}</span>
          </div>
        )}
      </div>
      {snapshot.items.length === 0
        ? <div className={styles.opsResult}>No items</div>
        : (
          <table className={styles.table}>
            <thead>
              <tr>
                <SortTh col="title" sort={sort} onSort={handleSort}>Title</SortTh>
                <SortTh col="domain" sort={sort} onSort={handleSort}>Domain</SortTh>
                <th>Author</th>
                <SortTh col="score" sort={sort} onSort={handleSort}>Score</SortTh>
                <SortTh col="publishedAt" sort={sort} onSort={handleSort}>Published</SortTh>
              </tr>
            </thead>
            <tbody>
              {displayed.map((item) => (
                <tr key={item.id}>
                  <td>
                    <a href={item.url} target="_blank" rel="noreferrer" className={styles.itemLink}>
                      {item.title}
                    </a>
                  </td>
                  <td>{item.domain}</td>
                  <td>{item.author ?? ""}</td>
                  <td>{item.score ?? ""}</td>
                  <td>{relativeTime(item.publishedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </div>
  );
}

export function SourceTable({ snapshot, sourceId, loading }: Props) {
  const [view, setView] = useState<"ux" | "json">("ux");

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Source Snapshot: {sourceId}</h2>
        {snapshot && (
          <div className={styles.viewToggle}>
            {(["ux", "json"] as const).map((v) => (
              <button
                key={v}
                type="button"
                className={`${styles.viewToggleBtn} ${view === v ? styles.viewToggleBtnActive : ""}`}
                onClick={() => setView(v)}
              >
                {v.toUpperCase()}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className={styles.panelBody}>
        {!snapshot
          ? <span className={styles.panelEmpty}>{loading ? "Loading…" : "Load a Source to see details."}</span>
          : view === "json"
            ? <JSONTree data={snapshot} />
            : <SourceTableContent snapshot={snapshot} />
        }
      </div>
    </div>
  );
}
